import { Icon } from '@ohif/ui';
import {
  ConfirmationModal,
  CustomTable,
  Failed,
  Loader,
  Search,
  Success,
} from '../../../../components';
import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadRelatedReportApi } from '../../../../rtk-apis/relatedReports/uploadRelatedReport';
import { deleteRelatedReportApi } from '../../../../rtk-apis/relatedReports/deleteRelatedReport';
import { getRelatedReportApi } from '../../../../rtk-apis/relatedReports/getRelatedReport';
import { clearState as clearUploadRelatedReportState } from '../../../../store/slices/relatedReports/uploadRelatedReport';
import { clearState as clearDeleteRelatedReportState } from '../../../../store/slices/relatedReports/deleteRelatedReport';
import { clearState as clearGetRelatedReportState } from '../../../../store/slices/relatedReports/getRelatedReport';
import { AppDispatch, RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getHospitalId } from '../../../../Utils/authUtils';
import { utils } from '@ohif/core';
import { useParams } from 'react-router-dom';

const { formatDate } = utils;

const PatientAttachments = ({ ReportData, setGetRelatedReportResponseCount }) => {
  const { _id } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const {
    status: uploadRelatedReportStatus,
    result: uploadRelatedReportResult,
    loading: uploadRelatedReportLoading,
    msg: uploadRelatedReportMsg,
  } = useSelector((state: RootState) => state.uploadRelatedReportResponse);
  const {
    status: getRelatedReportStatus,
    result: getRelatedReportResult,
    loading: getRelatedReportLoading,
    msg: getRelatedReportMsg,
  } = useSelector((state: RootState) => state.getRelatedReportResponse);
  const {
    status: deleteRelatedReportStatus,
    result: deleteRelatedReportResult,
    loading: deleteRelatedReportLoading,
    msg: deleteRelatedReportMsg,
  } = useSelector((state: RootState) => state.deleteRelatedReportResponse);

  const [preview, setPreview] = useState<string>('');
  const [prescription, setPrescription] = useState(null);
  const [prescriptionListData, setPrescriptionList] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState<boolean>(false);
  const [displayMessage, setDisplayMessage] = useState<string>('');
  const [loader, setLoader] = useState(false);

  const onDrop = useCallback(acceptedFiles => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file); // Convert the file to Base64
    }
    setPrescription(file);
  }, []);

  const handleUploadPrescription = async (event, folderPath) => {
    setLoader(true);
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const formData = new FormData();
        formData.append(
          'hospitalId',
          sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId')
        );
        formData.append('file', file);
        // formData.append('uhid', _id);
        formData.append('studyInstanceUID', ReportData?.data?.studyInstanceUID);

        dispatch(uploadRelatedReportApi(formData));
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleAccordion = (index, item) => {
    if (isPDF(item?.documentName)) {
      window.open(item?.documentData, '_blank', 'noopener,noreferrer');
    } else {
      setOpenIndex(openIndex === index ? null : index);
    }
  };

  function isPDF(documentName) {
    return documentName.toLowerCase().endsWith('.pdf');
  }

  const handlePrescriptionDelete = () => {
    setLoader(true);
    setIsConfirmationModalOpen(false);
    const obj = {
      prescriptionId: deleteId,
    };
    dispatch(deleteRelatedReportApi(obj));
  };

  useEffect(() => {
    setLoader(true);
    const payload = {
      // uhid: _id,
      studyInstanceUID: ReportData?.data?.studyInstanceUID,
      hospitalId: getHospitalId(),
      limit: 10,
      page: 1,
    };
    dispatch(getRelatedReportApi(payload));
  }, []);

  useEffect(() => {
    const handleSuccess = message => {
      setLoader(false);
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      const payload = {
        // uhid: _id,
        studyInstanceUID: ReportData?.data?.studyInstanceUID,
        hospitalId: getHospitalId(),
        limit: 10,
        page: 1,
      };
      setPrescriptionList(getRelatedReportResult);
      setGetRelatedReportResponseCount(getRelatedReportResult.length);
      dispatch(getRelatedReportApi(payload));
    };

    const handleFailure = message => {
      setLoader(false);
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };

    if (getRelatedReportStatus === 'SUCCESS') {
      setLoader(false);
      setPrescriptionList(getRelatedReportResult);
    }

    if (getRelatedReportStatus === 'FAILED') {
      handleFailure(getRelatedReportMsg);
    }

    if (uploadRelatedReportStatus === 'SUCCESS') {
      setPreview('');
      handleSuccess(uploadRelatedReportMsg);
    }

    if (uploadRelatedReportStatus === 'FAILED') {
      handleFailure(uploadRelatedReportMsg);
    }

    if (deleteRelatedReportStatus === 'SUCCESS') {
      handleSuccess(deleteRelatedReportMsg);
    }

    if (deleteRelatedReportStatus === 'FAILED') {
      handleFailure(deleteRelatedReportMsg);
    }

    return () => {
      dispatch(clearGetRelatedReportState());
      dispatch(clearDeleteRelatedReportState());
      dispatch(clearUploadRelatedReportState());
    };
  }, [getRelatedReportStatus, uploadRelatedReportStatus, deleteRelatedReportStatus]);

  return (
    <div>
      {prescriptionListData?.map((item, index) => {
        return (
          <div
            className="mb-2 rounded-lg"
            key={index}
          >
            <button
              onClick={() => toggleAccordion(index, item)}
              className="accordion w-full rounded-t-lg bg-gray-100 px-4 py-2 text-left hover:bg-gray-200"
            >
              <div
                className="flex"
                style={{ flexDirection: 'column' }}
              >
                <p className="accordion-date">{formatDate(item.createdAt)}</p>
                <p className="accordion-name">{item.documentName}</p>
              </div>

              <span className="flex items-center gap-4">
                <Icon
                  name="delete-icon"
                  onClick={e => {
                    e.stopPropagation();
                    setDeleteId(item._id);
                    setIsConfirmationModalOpen(true);
                  }}
                />
                <span className={`transition-transform ${openIndex === index ? 'rotate-180' : ''}`}>
                  <Icon name="chevron-down" />
                </span>
              </span>
            </button>
            {
              openIndex === index && (
                // (isPDF(item?.documentName) ? (
                //   <></>
                // ) : (
                // <div className="accordion-image border-t bg-white p-4 text-2xl">
                //   <a
                //     className=""
                //     href={item.documentData}
                //     target="_blank"
                //     rel="noopener noreferrer"
                //   >
                //     Open PDF
                //   </a>
                // </div>
                <div className="accordion-image border-t bg-white p-4">
                  <img
                    src={item.documentData}
                    alt="Prescription"
                  />
                </div>
              )
              // ))
            }
          </div>
        );
      })}
      <div className="tableWrap">
        <div className="relative cursor-pointer">
          <SignatureDropzone
            onDrop={acceptedFiles => onDrop(acceptedFiles)}
            preview={preview}
          />
        </div>
        <button
          className="btn btn__primary mt-4"
          onClick={() =>
            handleUploadPrescription({ target: { files: [prescription] } }, 'prescription')
          }
        >
          Upload
        </button>
      </div>
      <Loader isLoading={loader} />
      <Success
        isSuccessModalOpen={isSuccessModalOpen}
        title={displayMessage}
        handleCloseModal={() => setIsSuccessModalOpen(false)}
      />
      <Failed
        isFailedModalOpen={isFailedModalOpen}
        title="Prescription upload failed"
        handleCloseModal={() => setIsFailedModalOpen(false)}
      />
      <ConfirmationModal
        title="Delete Prescription"
        description="Are you sure you want to delete this prescription?"
        isConfirmationModalOpen={isConfirmationModalOpen}
        handleCloseModal={() => setIsConfirmationModalOpen(false)}
        handleConfirm={handlePrescriptionDelete}
        cancelActionText="Cancel"
        confirmActionText="Delete"
      />
    </div>
  );
};

export default PatientAttachments;

export const SignatureDropzone = ({ onDrop, preview }) => {
  const [error, setError] = useState(null);

  const handleDrop = acceptedFiles => {
    const maxSize = 50 * 1024 * 1024; // 50 MB
    const validFiles = [];

    setError(null);

    for (const file of acceptedFiles) {
      if (file.size <= maxSize) {
        validFiles.push(file);
      } else {
        // Remove previous image and show error
        onDrop([]); // Clear previous images
        setError(`File ${file.name} is too large. Maximum image size is 50 MB.`);
        break; // Exit loop after showing error
      }
    }

    if (validFiles.length > 0) {
      onDrop(validFiles); // Send valid files to the parent
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
      'application/xml': ['.xml'],
      'text/xml': ['.xml'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50 MB
    onDropRejected: rejections => {
      const code = rejections?.[0]?.errors?.[0]?.code;
      setError(
        code === 'file-invalid-type'
          ? 'Unsupported file type. Allowed: JPEG, PNG, PDF, XML.'
          : 'File is too large. Maximum size is 50 MB.'
      );
    },
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: '2px dashed #cccccc',
        padding: '20px',
        textAlign: 'center',
        marginBottom: '10px',
        width: '100%',
        height: '150px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: isDragActive ? '#496279' : '#26333F',
        position: 'relative',
        overflow: 'hidden',
        color: 'white',
      }}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center justify-center text-center">
        {!preview ? (
          <>
            <Icon
              name="Upload_Icon"
              alt="appointmentPreview"
            />
            <p>
              Drag & Drop files or{' '}
              <span
                className="main-theme-color underline"
                style={{ textDecoration: 'underline' }}
              >
                Browse
              </span>
            </p>
            <p>
              Supported format: <span className="main-theme-color">JPEG, PNG, PDF, XML</span>
            </p>
          </>
        ) : (
          <img
            src={preview}
            alt="Related Report Preview"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        )}
        {error && <p className="text-red-500">{error}</p>}
      </div>
    </div>
  );
};
